import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { usePeople } from '../../../hooks/usePeople';
import {
  useCancelPayroll,
  useConfirmPayroll,
  usePreviewPayroll,
} from '../../../hooks/useEmployment';
import type { PayrollPreviewResult } from '../../../lib/payrollApi';
import { ApiError } from '../../../lib/api';
import { useToast } from '../../tasks/linear/Toast';
import { FileDropzone } from '../../onboarding/FileDropzone';
import { Select } from '../../ui/Select';

// ---------------------------------------------------------------------------
// Two-phase payroll ("ștat de plată") upload dialog, modeled on
// ExcelImportDialog: pick a PDF (+ optional month/year override) → AI preview
// with name-matched, editable rows → confirm. Cancelling after a preview
// deletes the PENDING PayrollDocument (and the stored PDF) on the server.
// ---------------------------------------------------------------------------

const MAX_PAYROLL_BYTES = 10 * 1024 * 1024; // keep in sync with backend

type Step = 'pick' | 'review';

/** Editable row state during review; amounts kept as strings for the inputs. */
interface EditableRow {
  rowId: number;
  extractedName: string;
  personId: string | null;
  confidence: number;
  grossSalary: string;
  netSalary: string;
  employeeContributions: string;
  incomeTax: string;
  employerCam: string;
  totalCost: string;
}

const AMOUNT_FIELDS = [
  'grossSalary',
  'netSalary',
  'employeeContributions',
  'incomeTax',
  'employerCam',
  'totalCost',
] as const;

type AmountField = (typeof AMOUNT_FIELDS)[number];

const AMOUNT_LABEL_KEYS: Record<AmountField, string> = {
  grossSalary: 'people.payroll.gross',
  netSalary: 'people.payroll.net',
  employeeContributions: 'people.payroll.employeeContributions',
  incomeTax: 'people.payroll.incomeTax',
  employerCam: 'people.payroll.cam',
  totalCost: 'people.payroll.totalCost',
};

export interface PayrollUploadDialogProps {
  onClose: () => void;
}

export function PayrollUploadDialog({ onClose }: PayrollUploadDialogProps) {
  const { t } = useI18n();
  const toast = useToast();

  const { data: people } = usePeople({ active: 'true' });
  const preview = usePreviewPayroll();
  const confirm = useConfirmPayroll();
  const cancel = useCancelPayroll();

  const [step, setStep] = useState<Step>('pick');
  const [yearOverride, setYearOverride] = useState('');
  const [monthOverride, setMonthOverride] = useState('');
  const [pickError, setPickError] = useState<string | null>(null);

  const [result, setResult] = useState<PayrollPreviewResult | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);

  const peopleOptions = useMemo(
    () => (people ?? []).slice().sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [people],
  );

  const matchedCount = rows.filter((r) => r.personId).length;

  async function handleFile(file: File) {
    if (file.size > MAX_PAYROLL_BYTES) {
      setPickError(t('people.payroll.fileTooLarge'));
      return;
    }
    setPickError(null);
    try {
      const res = await preview.mutateAsync({
        file,
        year: yearOverride ? Number(yearOverride) : undefined,
        month: monthOverride ? Number(monthOverride) : undefined,
      });
      setResult(res);
      setRows(
        res.rows.map((row) => ({
          rowId: row.rowId,
          extractedName: row.extractedName,
          personId: row.personId,
          confidence: row.confidence,
          grossSalary: String(row.grossSalary),
          netSalary: String(row.netSalary),
          employeeContributions: String(row.employeeContributions),
          incomeTax: String(row.incomeTax),
          employerCam: String(row.employerCam),
          totalCost: String(row.totalCost),
        })),
      );
      setStep('review');
    } catch (e) {
      setPickError(e instanceof ApiError ? e.message : t('people.payroll.previewFailed'));
    }
  }

  function updateRow(rowId: number, patch: Partial<EditableRow>) {
    setRows((prev) => prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  }

  function removeRow(rowId: number) {
    setRows((prev) => prev.filter((row) => row.rowId !== rowId));
  }

  async function handleConfirm() {
    if (!result) return;
    // Rows without an associated person are dropped client-side (the backend
    // requires a personId per row).
    const payloadRows = rows
      .filter((row) => row.personId)
      .map((row) => ({
        personId: row.personId as string,
        extractedName: row.extractedName,
        grossSalary: Number(row.grossSalary) || 0,
        netSalary: Number(row.netSalary) || 0,
        employeeContributions: Number(row.employeeContributions) || 0,
        incomeTax: Number(row.incomeTax) || 0,
        employerCam: Number(row.employerCam) || 0,
        totalCost: Number(row.totalCost) || 0,
      }));
    if (payloadRows.length === 0) return;
    // Same person selected on two rows would collide on the [person, month]
    // unique key — surface it before the backend rejects it.
    const ids = payloadRows.map((r) => r.personId);
    if (new Set(ids).size !== ids.length) {
      toast.show(t('people.payroll.duplicatePerson'));
      return;
    }
    try {
      const res = await confirm.mutateAsync({
        documentId: result.documentId,
        input: { year: result.year, month: result.month, rows: payloadRows },
      });
      toast.show(t('people.payroll.confirmed', { count: res.entriesWritten }));
      onClose();
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : t('people.payroll.confirmFailed'));
    }
  }

  function handleClose() {
    // Cancelling after a preview removes the PENDING document + stored PDF.
    if (result && step === 'review') {
      cancel.mutate(result.documentId);
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t('people.payroll.dialogTitle')}
            {result && step === 'review' && (
              <span className="ml-2 font-normal text-gray-500">
                {String(result.month).padStart(2, '0')}/{result.year}
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {step === 'pick' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{t('people.payroll.pickIntro')}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="text-sm text-gray-600">
                  {t('people.payroll.monthOverride')}
                  <Select
                    label={t('people.payroll.monthOverride')}
                    hideLabel
                    clearable
                    value={monthOverride}
                    onChange={setMonthOverride}
                    placeholder={t('people.payroll.autoDetect')}
                    options={Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
                      value: String(m),
                      label: String(m).padStart(2, '0'),
                    }))}
                    className="mt-1"
                  />
                </div>
                <div className="text-sm text-gray-600">
                  {t('people.payroll.yearOverride')}
                  <Select
                    label={t('people.payroll.yearOverride')}
                    hideLabel
                    clearable
                    value={yearOverride}
                    onChange={setYearOverride}
                    placeholder={t('people.payroll.autoDetect')}
                    options={Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(
                      (y) => ({ value: String(y), label: String(y) }),
                    )}
                    className="mt-1"
                  />
                </div>
              </div>
              {preview.isPending ? (
                <p className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t('people.payroll.parsing')}
                </p>
              ) : (
                <FileDropzone
                  accept=".pdf,application/pdf"
                  hint={t('people.payroll.pickHint')}
                  onFile={(file) => void handleFile(file)}
                />
              )}
              {pickError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{pickError}</p>
              )}
            </div>
          )}

          {step === 'review' && result && (
            <div className="space-y-4">
              {result.warnings.length > 0 && (
                <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  {result.warnings.map((warning, i) => (
                    <p key={i} className="flex items-start gap-1.5">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {warning}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-sm text-gray-600">
                {t('people.payroll.reviewIntro', {
                  matched: matchedCount,
                  total: rows.length,
                })}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-3">{t('people.payroll.extractedName')}</th>
                      <th className="py-2 pr-3">{t('people.payroll.matchedPerson')}</th>
                      {AMOUNT_FIELDS.map((field) => (
                        <th key={field} className="py-2 pr-3 text-right">
                          {t(AMOUNT_LABEL_KEYS[field])}
                        </th>
                      ))}
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.rowId}
                        className={`border-b border-gray-100 last:border-0 ${
                          row.personId ? '' : 'bg-amber-50'
                        }`}
                      >
                        <td className="max-w-[10rem] truncate py-2 pr-3 font-medium text-gray-800">
                          {row.extractedName}
                        </td>
                        <td className="py-2 pr-3">
                          <Select
                            label={t('people.payroll.matchedPerson')}
                            hideLabel
                            size="sm"
                            clearable
                            searchable
                            value={row.personId ?? ''}
                            onChange={(v) => updateRow(row.rowId, { personId: v || null })}
                            placeholder={t('people.payroll.unmatched')}
                            options={peopleOptions.map((person) => ({
                              value: person.id,
                              label: person.fullName,
                            }))}
                            className="w-40"
                          />
                        </td>
                        {AMOUNT_FIELDS.map((field) => (
                          <td key={field} className="py-2 pr-3">
                            <input
                              type="number"
                              step="0.01"
                              value={row[field]}
                              onChange={(e) => updateRow(row.rowId, { [field]: e.target.value })}
                              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                            />
                          </td>
                        ))}
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeRow(row.rowId)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                            aria-label={t('people.payroll.removeRow')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.some((row) => !row.personId) && (
                <p className="text-xs text-amber-600">{t('people.payroll.unmatchedHint')}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          {step === 'review' && (
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={confirm.isPending || matchedCount === 0}
              className="flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {confirm.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('people.payroll.confirm', { count: matchedCount })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
